/**
 * WheelQRCode — a scannable QR code for a Wheel's public URL, rendered as pure
 * inline SVG (zero raster), with the iYuga brand Wheel mark tastefully in the
 * centre quiet zone. Wave 5 "the share moment".
 *
 * Encoder: the in-tree `qrcode.react` (QRCodeSVG) — no new dependency. Error
 * correction level "H" (30% recovery) so the centre brand mark never threatens
 * scannability. Dark modules use the brand navy (colors.primary #123051) on
 * white for a strong, on-brand, WCAG-AA contrast (~13:1, measured in tests).
 *
 * Centre mark: the canonical brandWheelSVG() (G-19 compliant — never a
 * hand-rolled wheel), inlined as real vector elements (not an <image href>), so
 * the whole SVG stays self-contained and rasterizes to PNG without tainting the
 * canvas.
 *
 * A11y: role="img" + aria-label carrying the full URL is the QR's text
 * alternative; a <title>/<desc> pair backs it for assistive tech.
 */
import React, { forwardRef } from "react";
import { QRCodeSVG } from "qrcode.react";
import { brandWheelInner } from "../BrandWheel/brandWheel";
import { colors } from "../../utils/theme";

// Fraction of the QR width the brand mark + its white excavation disc occupy.
// ~0.20 keeps the covered module area well under level-H's 30% tolerance.
const MARK_FRACTION = 0.2;
const DISC_FRACTION = 0.26; // white excavation disc, a touch larger than the mark

const WheelQRCode = forwardRef(function WheelQRCode(
  {
    url,
    size = 200,
    showBrandMark = true,
    idPrefix = "wheel-qr",
    label = "QR code for this Wheel",
  },
  ref
) {
  const S = size;
  const markSize = Math.round(S * MARK_FRACTION);
  const discR = (S * DISC_FRACTION) / 2;
  const c = S / 2;
  const titleId = `${idPrefix}-title`;
  const descId = `${idPrefix}-desc`;

  return (
    <svg
      ref={ref}
      width={S}
      height={S}
      viewBox={`0 0 ${S} ${S}`}
      role="img"
      aria-labelledby={`${titleId} ${descId}`}
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: "block" }}
    >
      <title id={titleId}>{label}</title>
      <desc id={descId}>{`Scan to open ${url}`}</desc>

      {/* The QR matrix. includeMargin adds the 4-module quiet zone scanners need. */}
      <QRCodeSVG
        value={url}
        size={S}
        level="H"
        includeMargin
        bgColor="#FFFFFF"
        fgColor={colors.primary}
      />

      {showBrandMark && (
        <g aria-hidden="true">
          {/* White excavation disc so the brand mark reads cleanly and the
              covered modules become known erasures level H recovers. */}
          <circle cx={c} cy={c} r={discR} fill="#FFFFFF" />
          <svg
            x={c - markSize / 2}
            y={c - markSize / 2}
            width={markSize}
            height={markSize}
            viewBox="0 0 120 120"
            // eslint-disable-next-line react/no-danger -- static, trusted brand SVG string (no user input)
            dangerouslySetInnerHTML={{ __html: brandWheelInner({ disc: true }) }}
          />
        </g>
      )}
    </svg>
  );
});

export default WheelQRCode;
